import type { SetterOrUpdaterParam } from "@mdotm/shadcn/legacy/utils";
import { toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { type ConceptParameterForm, timeSeriesFormOf } from "../chart-type";
import type { WorkspaceChartTypeEnum } from "../spec-gaps";
import type { ConceptDefinitionDTO } from "@eu.mdotm.api/smart-workspaces";
import { maybeAppendAutogeneratedName } from "../common";
import { offeredRepresentationModes, relativenessKeptFor } from "../representation-mode";
import { breakdownAllOption, tagBreakdownDimension } from "./breakdown";
import { ChainableElement } from "./ChainableElement";
import { chainForChartType, chainForPickedConcept } from "./rules";
import type { WorkspaceChain } from "./type";

/**
 * # One chain
 *
 * Every chain in this area is the same sequence: pick a concept, then answer whatever that concept
 * leaves open — its tag or risk-model exposure, its confidence, its time horizon, its source, its
 * relativeness. The chart panel, the column panel and the filter panel used to each carry their own copy
 * of that sequence, which is how the two builders drifted into disagreeing about the order of two of the
 * steps and about which of them a non-`NUMERIC` concept gets at all.
 *
 * So the sequence lives here once, as {@link conceptSteps} — one entry per step, each gated on the
 * concept publishing the list it offers — and a caller supplies two things:
 *
 * - **the order**, because the panels genuinely do not agree on one (see {@link chartStepOrder} and
 *   {@link filterStepOrder}), and a difference that is real is better stated as data than buried in two
 *   files;
 * - **a tail**, for the steps that are not about parameterizing the concept at all. The filter's
 *   condition — an operator and a value — is the only one, exactly as the chart items take a chart-type spec
 *   for the part that is theirs alone.
 *
 * The steps themselves are not rewritten: they are the components in `ChainableElement.tsx`, called with
 * the same props they were called with before. Sharing a sequence does not require rewriting what is in
 * it.
 */

/** One parameter step, by name — the vocabulary the two orders are written in. */
export type ConceptStepId =
	| "tagExposure"
	| "riskModelExposure"
	| "confidence"
	| "timeHorizon"
	| "source"
	| "relativeness";

/**
 * The order the chart and column panels show the parameter steps in.
 *
 * The source select sits *between* the time horizon and the relativeness pair here, and *after* the
 * relativeness pair in {@link filterStepOrder}. That is the one thing the two builders disagreed about,
 * and it is preserved rather than reconciled: which of the two is right is a design question, and this
 * commit is not the place to answer it.
 */
export const chartStepOrder: ReadonlyArray<ConceptStepId> = [
	"tagExposure",
	"riskModelExposure",
	"confidence",
	"timeHorizon",
	"source",
	"relativeness",
];

/** The order the filter panel shows them in — the source select last, after the relativeness pair. */
export const filterStepOrder: ReadonlyArray<ConceptStepId> = [
	"tagExposure",
	"riskModelExposure",
	"confidence",
	"timeHorizon",
	"relativeness",
	"source",
];

/** How a step reports a change: the chain it produces, with the name regenerated if the panel asks. */
type ChainUpdate<C extends WorkspaceChain> = (mutate: (latest: C) => C) => void;

/**
 * Everything a parameter step needs in order to decide whether it applies and to render itself.
 *
 * Typed against {@link WorkspaceChain} rather than against the caller's chain type: every step reads and
 * writes the concept's *own* parameters and spreads the rest through untouched, so nothing here has to
 * know that a filter chain also carries a predicate. {@link ConceptChain} is where that is reconciled,
 * with the one cast the arrangement costs.
 */
export type ConceptStepContext = {
	chain: WorkspaceChain;
	/** The picked concept. Non-null: the chain renders nothing past the concept select without one. */
	concept: ConceptDefinitionDTO;
	/**
	 * Which values the concept accepts *for the chart type being described* — see {@link timeSeriesFormOf}.
	 * Every step reads this rather than the concept's own fields, because a line chart draws the concept's
	 * history and the history's sources, horizons and representation modes are its own.
	 */
	form: ConceptParameterForm;
	/** Offer the "All" leaf on every level the concept declares breakable. Chart-only. */
	allowBreakdown: boolean;
	/** Suppress the source select, for a panel that resolves the source itself. */
	hideSource: boolean;
	/** Applies a change and regenerates the auto-generated name from the result. */
	update: ChainUpdate<WorkspaceChain>;
};

/**
 * What a tail step gets. Its own chain type, because the fields a tail writes are the ones the shared
 * steps know nothing about.
 */
export type ConceptChainTailContext<C extends WorkspaceChain> = {
	chain: C;
	/** The picked concept. Non-null, as in {@link ConceptStepContext}. */
	concept: ConceptDefinitionDTO;
	/**
	 * Applies a change. Deliberately *without* regenerating the name: an auto-generated name is built from
	 * the concept and its parameters (see `autoGenerateMetricName`) and never mentions the tail, so
	 * regenerating it here would only ever produce the same string.
	 */
	update: ChainUpdate<C>;
};

/** The steps a panel appends after the parameter steps, and what a freshly picked concept seeds for them. */
export type ConceptChainTail<C extends WorkspaceChain> = {
	/**
	 * The fields a freshly picked concept seeds beyond its own parameterization — the tail's half of
	 * {@link chainForPickedConcept}.
	 */
	seed(concept: ConceptDefinitionDTO | undefined): Partial<C>;
	/** The steps themselves, in order, or none when the concept has nothing for the tail to ask. */
	steps(ctx: ConceptChainTailContext<C>): ReactNode[];
};

/**
 * The parameter steps: one entry per step, keyed on the name the orders above use.
 *
 * Each is gated on the concept publishing the list the step offers, and on nothing else. That is what the
 * chart builder always did; the filter builder wrapped the whole group in `dataType === "NUMERIC"` as
 * well, which was redundant for every concept a catalogue actually publishes — horizons, confidences and
 * tag exposures parameterize metrics — and which is dropped here so that one gate answers the question
 * for both panels.
 */
const conceptSteps = {
	tagExposure: (ctx) => {
		const tagsConcepts = ctx.chain.availableTagsConcepts;
		if (!tagsConcepts || tagsConcepts.length === 0) {
			return null;
		}
		return (
			<ChainableElement.SelectHierarchicalConceptExposureOption
				key="step-tag-exposure"
				tagsConcepts={tagsConcepts}
				allowBreakdown={ctx.allowBreakdown}
				value={
					ctx.chain.parameters?.tagExposureOption || ctx.chain.breakdown
						? {
								id: ctx.concept.id!,
								opt: ctx.chain.breakdown ? breakdownAllOption : ctx.chain.parameters!.tagExposureOption!,
							}
						: null
				}
				onChange={(newTagConceptOpt) =>
					ctx.update((latest) => {
						// "All" expands the tag instead of pinning one of its options, so the two are written to
						// different fields and each clears the other.
						const isBreakdown = newTagConceptOpt?.opt === breakdownAllOption;
						const dimension = isBreakdown
							? tagBreakdownDimension(latest.availableTagsConcepts?.find((c) => c.id === newTagConceptOpt?.id))
							: undefined;
						return {
							...latest,
							concept: latest.concept
								? { ...latest.concept, id: newTagConceptOpt?.id ?? latest.concept.id! }
								: undefined,
							parameters: {
								tagExposureOption: isBreakdown ? undefined : newTagConceptOpt?.opt,
							},
							breakdown: dimension ? { dimension } : undefined,
						};
					})
				}
			/>
		);
	},
	riskModelExposure: (ctx) => {
		if (ctx.concept.dataType !== "NUMERIC" || ctx.concept.id !== "systemId: riskModel") {
			return null;
		}
		return (
			<ChainableElement.SelectConceptExposureRiskModel
				key="step-risk-model-exposure"
				conceptId={ctx.concept.id}
				initialValue={ctx.chain.parameters}
				breakdownDimensions={ctx.allowBreakdown ? ctx.concept.breakdownDimensions : undefined}
				breakdownDimension={ctx.chain.breakdown?.dimension}
				onChange={(newRiskModelExposureLevels, breakdownDimension) =>
					ctx.update((latest) => ({
						...latest,
						parameters: newRiskModelExposureLevels,
						breakdown: breakdownDimension ? { dimension: breakdownDimension } : undefined,
					}))
				}
			/>
		);
	},
	confidence: (ctx) => {
		if (!ctx.form.allowedConfidenceLevels || ctx.form.allowedConfidenceLevels.length === 0) {
			return null;
		}
		return (
			<ChainableElement.SelectConceptConfidence
				key="step-confidence"
				value={ctx.chain.confidenceLevel ?? null}
				allowedConfidence={ctx.form.allowedConfidenceLevels}
				onChange={(newConfidence) =>
					ctx.update((latest) => ({ ...latest, confidenceLevel: newConfidence ?? undefined }))
				}
			/>
		);
	},
	timeHorizon: (ctx) => {
		if (!ctx.form.allowedTimeHorizons || ctx.form.allowedTimeHorizons.length === 0) {
			return null;
		}
		return (
			<ChainableElement.SelectConceptTimeHorizon
				key="step-time-horizon"
				value={ctx.chain.timeHorizon ?? null}
				allowedTimeHorizons={ctx.form.allowedTimeHorizons}
				onChange={(newTimeHorizon) => ctx.update((latest) => ({ ...latest, timeHorizon: newTimeHorizon ?? undefined }))}
			/>
		);
	},
	source: (ctx) => {
		// Only a concept that resolves against more than the current portfolio has a source to pick, and
		// `PROPOSAL` is the one that makes it a choice.
		if (!ctx.form.allowedSource?.includes("PROPOSAL") || ctx.hideSource) {
			return null;
		}
		return (
			<ChainableElement.SelectConceptSource
				key="step-source"
				allowedSource={ctx.form.allowedSource}
				value={ctx.chain.source ?? null}
				onChange={(newSource) => ctx.update((latest) => ({ ...latest, source: newSource }))}
			/>
		);
	},
	relativeness: (ctx) => {
		if (
			ctx.concept.dataType !== "NUMERIC" ||
			!ctx.form.representationModes ||
			ctx.form.representationModes.length === 0
		) {
			return null;
		}
		return (
			<ChainableElement.SelectConceptRelativeness
				key="step-relativeness"
				allowedParameterizedConceptModes={ctx.form.relativenessModes}
				relativenessModeOptions={offeredRepresentationModes(ctx.form)}
				relativenessMode={ctx.chain.representationMode ?? null}
				onChangeRelativenessMode={(representationMode) =>
					ctx.update((latest) => ({
						...latest,
						representationMode,
						// A dropped reference leaves the picker empty, which then adopts the mode's default (`defaultsToOwnReference`).
						parameterizedRelativeness: relativenessKeptFor(
							representationMode,
							latest.parameterizedRelativeness,
							ctx.form.relativenessModes,
						),
					}))
				}
				relativeness={
					ctx.chain.parameterizedRelativeness
						? {
								...ctx.chain.parameterizedRelativeness,
								// The "compare against each portfolio" option has no uuid on the wire but the select
								// needs one to hold a selection, so it carries a synthetic one while the chain is open.
								uuid:
									ctx.chain.parameterizedRelativeness.uuid === undefined
										? `SELF_${ctx.chain.parameterizedRelativeness.mode}`
										: ctx.chain.parameterizedRelativeness.uuid,
							}
						: undefined
				}
				onChangeRelativenessEntity={(newRelativenessEntity) =>
					ctx.update((latest) => ({
						...latest,
						parameterizedRelativeness: newRelativenessEntity?.uuid?.includes("SELF")
							? { ...newRelativenessEntity, uuid: undefined }
							: newRelativenessEntity || undefined,
					}))
				}
				conceptId={ctx.concept.id!}
			/>
		);
	},
} satisfies Record<ConceptStepId, (ctx: ConceptStepContext) => ReactNode>;

export type ConceptChainProps<C extends WorkspaceChain> = {
	initialValue?: C;
	onChange(newChain: C): void;
	autogenerateName: boolean;
	allowedConcepts?: "ALL" | "METRICS_ONLY" | "PROPERTIES_ONLY";
	/** Grey out the concepts a panel cannot use. */
	optionsToDisable?(concept: ConceptDefinitionDTO): boolean;
	hideConceptSourceSelection?: boolean; // remove this in favor of multiple source chain
	/**
	 * Offer the "All" leaf on every exposure level the concept declares breakable, turning the last step
	 * of the chain into "expand this level" instead of "pin it" — which is the whole of the
	 * stacked-column configuration. See {@link breakdownAllOption}.
	 */
	allowBreakdown?: boolean;
	/**
	 * Which chart the chain is describing, because that decides *which parameters the concept accepts* —
	 * see {@link timeSeriesFormOf}. `PERFORMANCE_LINE` asks for the concept's history, whose sources,
	 * horizons and representation modes are its own; every other chart type asks for its single value.
	 *
	 * Optional and `HISTOGRAM` by default, so the table's column and filter pickers — which never draw a
	 * line — keep reading the concept's own fields with no argument to pass.
	 */
	chartType?: WorkspaceChartTypeEnum;
	/** In which order the parameter steps are shown. */
	stepOrder?: ReadonlyArray<ConceptStepId>;
	/** What comes after them, if anything. */
	tail?: ConceptChainTail<C>;
};

export function ConceptChain<C extends WorkspaceChain>(props: ConceptChainProps<C>): ReactNode[] {
	const { allowedConcepts = "ALL", chartType = "HISTOGRAM", stepOrder = chartStepOrder, tail } = props;
	// Every write regenerates the name, so the two flags that decide how travel together.
	const nameOpts = { autoGenerateName: props.autogenerateName, ignoreSource: props.hideConceptSourceSelection };
	const [chain, setChain] = useState<C>(props.initialValue ?? ({} as C));
	function _onChange(setterOrUpdater: SetterOrUpdaterParam<C>) {
		setChain((latest) => {
			const result = typeof setterOrUpdater === "function" ? setterOrUpdater(latest) : setterOrUpdater;
			props.onChange(result);
			return result;
		});
	}

	// Switching the chart type switches which parameters are on offer, so a value that was legal under
	// the previous type can outlive it — see {@link chainForChartType}. Guarded on the type actually having
	// changed, so it never fights a value the user just picked.
	const previousChartType = useRef(chartType);
	// biome-ignore lint/correctness/useExhaustiveDependencies: the chart type is a trigger-only dependency — everything else is read fresh inside the updater, and `_onChange` is a new closure each render
	useEffect(() => {
		if (previousChartType.current === chartType) {
			return;
		}
		previousChartType.current = chartType;
		_onChange((latest) => chainForChartType(latest, chartType, nameOpts) as C);
	}, [chartType]);

	// What the concept select gives up half its row for is the time-horizon step: it is the payload guard
	// for a line and the metric's own parameterization otherwise.
	const parameterizedLikeAMetric = chain.concept?.dataType === "NUMERIC";

	const steps: ReactNode[] = [
		<div
			key="step-1"
			className={toClassName({
				"col-span-2": !chain.concept || !parameterizedLikeAMetric,
				"col-span-1": parameterizedLikeAMetric,
			})}
		>
			<ChainableElement.SelectConcept
				value={chain.fakeConceptId || chain.concept?.id || null}
				metricsOnly={allowedConcepts === "METRICS_ONLY"}
				optionsToDisable={props.optionsToDisable}
				onChange={(picked) => {
					if (picked == null) {
						_onChange({} as C);
						return;
					}
					_onChange({
						...(chainForPickedConcept(picked, chartType, { ...nameOpts, name: chain.name }) as C),
						...tail?.seed(picked.concept),
					});
				}}
			/>
		</div>,
	];

	if (chain.concept?.id == null) {
		return steps;
	}

	const stepContext: ConceptStepContext = {
		chain,
		concept: chain.concept,
		form: timeSeriesFormOf(chain.concept, chartType),
		allowBreakdown: props.allowBreakdown ?? false,
		hideSource: props.hideConceptSourceSelection ?? false,
		// The one cast the arrangement costs: a step is written against the concept's own fields and always
		// spreads `latest`, so whatever else the caller's chain carries comes out the other side unchanged —
		// which is true of every step above and is not expressible without making all of them generic.
		update: (mutate) => _onChange((latest) => maybeAppendAutogeneratedName(mutate(latest) as C, nameOpts)),
	};

	for (const id of stepOrder) {
		const step = conceptSteps[id](stepContext);
		if (step) {
			steps.push(step);
		}
	}

	if (tail) {
		steps.push(
			...tail.steps({
				chain,
				concept: chain.concept,
				update: (mutate) => _onChange((latest) => maybeAppendAutogeneratedName(mutate(latest))),
			}),
		);
	}

	return steps;
}
